import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';

const EDITABLE = ['name', 'company_name', 'email', 'phone', 'notes'];

export default function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [pubBusy, setPubBusy] = useState(false);
  const [pubCopied, setPubCopied] = useState(false);
  const [openPos, setOpenPos] = useState([]);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const canManage = user && (user.role === 'admin' || user.role === 'manager');

  async function load() {
    const res = await apiFetch(`/api/vendors/${id}`);
    if (res.ok) {
      const d = await res.json();
      setVendor(d.vendor);
      apiFetch(`/api/pos/by-vendor/${id}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((pd) => { if (pd) setOpenPos(pd.purchase_orders || []); })
        .catch(() => {});
      const f = {};
      EDITABLE.forEach((k) => { f[k] = d.vendor[k] || ''; });
      setForm(f);
    } else if (res.status === 404) { setMsg('Vendor not found.'); }
  }
  useEffect(() => { load(); }, [id]);

  async function save() {
    setMsg('');
    const res = await apiFetch(`/api/vendors/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) { const d = await res.json(); setVendor(d.vendor); setMsg('Saved. This vendor is now locally overridden.'); }
    else { const d = await res.json().catch(() => ({})); setMsg(d.error || 'Could not save.'); }
  }

  async function togglePublic(enabled) {
    setPubBusy(true);
    try {
      const res = await apiFetch(`/api/vendors/${id}/public-toggle`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.ok) { const d = await res.json(); setVendor((v) => ({ ...v, public_enabled: d.public_enabled, public_token: d.public_token })); }
    } finally { setPubBusy(false); }
  }
  function publicUrl() {
    if (!vendor || !vendor.public_token) return '';
    return `${window.location.origin}/public/vendor/${vendor.public_token}`;
  }
  function copyPublic() {
    const u = publicUrl(); if (!u) return;
    navigator.clipboard.writeText(u).then(() => { setPubCopied(true); setTimeout(() => setPubCopied(false), 2000); }).catch(() => {});
  }

  function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }
  function poMoney(n, cur) { if (n == null) return '—'; try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur || 'USD' }).format(n); } catch { return String(n); } }
  function poDate(s) { if (!s) return '—'; try { return new Date(s).toLocaleDateString(); } catch { return '—'; } }


  if (msg && !vendor) return <div className="hq-page-content"><div className="hq-page-head"><h1>Vendors</h1><p>{msg}</p></div></div>;
  if (!vendor) return <div className="hq-page-content"><div className="hq-page-head"><p>Loading…</p></div></div>;

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>{vendor.name || vendor.company_name || 'Vendor'}</h1>
        <p>
          {vendor.locally_overridden ? 'Locally overridden' : `Source: ${vendor.source}`}
          {vendor.last_synced_at ? ` · last synced ${fmt(vendor.last_synced_at)}` : ''}
        </p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Details</h2>
          {canManage && <button className="hq-btn" onClick={save}>Save</button>}
        </div>
        <div style={{ padding: 20 }}>
          {canManage ? (
            <div className="hq-form">
              <input className="hq-input" placeholder="Name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className="hq-input" placeholder="Company" value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
              <input className="hq-input" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className="hq-input" placeholder="Phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input className="hq-input" placeholder="Notes" value={form.notes} style={{ minWidth: 280 }}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          ) : (
            <table className="hq-table">
              <tbody>
                <tr><td className="hq-role">Name</td><td>{vendor.name || '—'}</td></tr>
                <tr><td className="hq-role">Company</td><td>{vendor.company_name || '—'}</td></tr>
                <tr><td className="hq-role">Email</td><td>{vendor.email || '—'}</td></tr>
                <tr><td className="hq-role">Phone</td><td>{vendor.phone || '—'}</td></tr>
                <tr><td className="hq-role">Notes</td><td>{vendor.notes || '—'}</td></tr>
              </tbody>
            </table>
          )}
          {msg && <p className="hq-msg">{msg}</p>}
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>From Zoho Books</h2></div>
        <table className="hq-table">
          <tbody>
            <tr><td className="hq-role">Status</td><td>{vendor.status || '—'}</td></tr>
            <tr><td className="hq-role">Payment terms</td><td>{vendor.payment_terms || '—'}</td></tr>
            <tr><td className="hq-role">Currency</td><td>{vendor.currency_code || '—'}</td></tr>
            <tr><td className="hq-role">Outstanding payable</td><td>{vendor.outstanding != null ? vendor.outstanding : '—'}</td></tr>
            <tr><td className="hq-role">Zoho contact ID</td><td>{vendor.zoho_contact_id || '—'}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head"><h2>Open purchase orders</h2></div>
        <table className="hq-table">
          <thead><tr><th>PO #</th><th>Order date</th><th>Delivery</th><th>Total</th></tr></thead>
          <tbody>
            {openPos.map((p) => (
              <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/purchase-orders/${p.id}`)}>
                <td>{p.po_number || '—'}</td>
                <td>{poDate(p.order_date)}</td>
                <td>{poDate(p.delivery_date)}</td>
                <td>{poMoney(p.total, p.currency_code)}</td>
              </tr>
            ))}
            {openPos.length === 0 && (
              <tr><td className="hq-empty" colSpan={4}>No open POs for this vendor.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {canManage && (
        <div className="hq-panel" style={{ marginBottom: 20 }}>
          <div className="hq-panel-head">
            <h2>Public production view</h2>
            <button className={`hq-btn${vendor.public_enabled ? ' hq-btn-ghost' : ''}`} disabled={pubBusy}
              onClick={() => togglePublic(!vendor.public_enabled)}>
              {pubBusy ? '…' : (vendor.public_enabled ? 'Disable' : 'Enable')}
            </button>
          </div>
          <div style={{ padding: 20 }}>
            {vendor.public_enabled && vendor.public_token ? (
              <>
                <p style={{ marginTop: 0, color: 'var(--hq-muted)', fontSize: 13 }}>
                  Share this link with {vendor.name || 'the vendor'}. They can view and update their jobs.
                  Disabling instantly kills this link.
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input className="hq-input" readOnly value={publicUrl()} style={{ flex: 1, fontFamily: 'monospace', fontSize: 12 }} />
                  <button className="hq-btn" onClick={copyPublic}>{pubCopied ? 'Copied!' : 'Copy'}</button>
                </div>
              </>
            ) : (
              <p className="hq-empty" style={{ padding: 0 }}>Public view is off. Enable to generate a shareable link.</p>
            )}
          </div>
        </div>
      )}

      <button className="hq-signout" style={{ width: 'auto', padding: '0 16px' }} onClick={() => navigate('/vendors')}>Back</button>
    </div>
  );
}
